import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const EventsWrapper = styled.div`
  margin: 4rem 0;
`;

const EventCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2.5rem;
  margin-bottom: 2.5rem;
  display: flex;
  flex-direction: column;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const EventImageContainer = styled.div`
  width: 100%;
  margin-bottom: 2rem;
  
  @media (min-width: 768px) {
    width: 40%;
    margin-bottom: 0;
    margin-right: 2.5rem;
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  max-height: 300px;
`;

const EventContent = styled.div`
  flex: 1;
`;

const EventTitle = styled.h3`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const EventDate = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #e0e0e0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.8rem;
    color: #0d9488;
  }
`;

const EventLocation = styled.p`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  color: #e0e0e0;
  display: flex;
  align-items: center;
  
  svg {
    margin-right: 0.8rem;
    color: #0d9488;
  }
`;

const EventDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: #e0e0e0;
`;

const EventButton = styled(Button)`
  align-self: flex-start;
  margin-top: 1.5rem;
`;

const OrganizeSection = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 3rem;
  margin: 5rem 0;
  text-align: center;
`;

const OrganizeTitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  color: #0d9488;
  font-style: italic;
`;

const OrganizeDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const TrainingEvents: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const upcomingEvents = [
    {
      image: 'https://images.unsplash.com/photo-1596120236172-231dbd3a72f2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: '850 years of the Tower of Pisa',
      date: language === 'it' ? 'Data da definire' : 'Time to be defined',
      location: 'Pisa, Piazza del Duomo, 56126 Pisa PI, Italy',
      description: language === 'it' 
        ? 'Un evento speciale che celebra l\'850° anniversario della Torre di Pisa, un\'occasione unica per esplorare la storia e l\'architettura di questo monumento iconico.'
        : 'A special event celebrating the 850th anniversary of the Tower of Pisa, a unique opportunity to explore the history and architecture of this iconic monument.'
    },
    {
      image: 'https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80',
      title: 'Lucca Comics & Games 2023',
      date: language === 'it' ? '1 Nov 2023, 8:00 - 5 Nov 2023, 23:00' : 'Nov 01, 2023, 8:00 AM – Nov 05, 2023, 11:00 PM',
      location: 'Lucca, 55100 Lucca LU, Italy',
      description: language === 'it' 
        ? 'Uno dei più grandi festival internazionali dedicati al fumetto, all\'animazione, ai giochi, ai videogiochi e all\'immaginario fantasy e fantascientifico.'
        : 'One of the largest international festivals dedicated to comics, animation, games, video games and fantasy and science fiction imagery.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Eventi Formativi | J-O-H' : 'Training Events | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Eventi formativi, workshop e conferenze sul settore degli affitti brevi e della gestione immobiliare' 
            : 'Training events, workshops and conferences on the short-term rental sector and property management'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/training">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'EVENTI FORMATIVI' : 'TRAINING EVENTS'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Siamo entusiasti di condividere con voi l\'ampia gamma di esperienze culturali, sportive ed enogastronomiche che la nostra zona ha da offrire. Qui troverete un elenco di eventi imminenti e potrete anche vedere quelli passati, non perdete la prossima opportunità!'
              : 'We are excited to share with you the wide range of cultural, sporting and food and wine experiences that our area has to offer. Here you will find a list of upcoming events and you can also see past ones, don\'t miss the next opportunity!'}
          </Description>
          
          <SectionTitle>
            {language === 'it' ? 'PROSSIMI EVENTI' : 'COMING UP NEXT'}
          </SectionTitle>
          
          <Description>
            {language === 'it' 
              ? 'Cogli l\'opportunità, non perderti i prossimi eventi'
              : 'Take the opportunity, don\'t miss the next events'}
          </Description>
          
          <EventsWrapper>
            {upcomingEvents.map((event, index) => (
              <EventCard key={index}>
                <EventImageContainer>
                  <EventImage src={event.image} alt={event.title} />
                </EventImageContainer>
                <EventContent>
                  <EventTitle>{event.title}</EventTitle>
                  <EventDate>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    {event.date}
                  </EventDate>
                  <EventLocation>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    {event.location}
                  </EventLocation>
                  <EventDescription>{event.description}</EventDescription>
                  <EventButton variant="outline">
                    {language === 'it' ? 'Dettagli' : 'Details'}
                  </EventButton>
                </EventContent>
              </EventCard>
            ))}
          </EventsWrapper>
          
          <OrganizeSection>
            <OrganizeTitle>
              {language === 'it' ? 'VUOI ORGANIZZARE UN EVENTO?' : 'DO YOU WANT TO ORGANIZE AN EVENT?'}
            </OrganizeTitle>
            <OrganizeDescription>
              {language === 'it' 
                ? 'La nostra missione è trasformare le tue idee in realtà. Potrai organizzare vari tipi di eventi: matrimonio, battesimo, comunione, festa aziendale, lancio di prodotto e molto altro.'
                : 'Our mission is to turn your ideas into reality. You will be able to organize various types of events: wedding, baptism, communion, company party, product launch and much more.'}
            </OrganizeDescription>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci' : 'Contact us'}
            </Button>
          </OrganizeSection>
          
          <CTASection>
            <Description>
              {language === 'it' 
                ? 'Partecipa ai nostri eventi formativi per ampliare le tue conoscenze, fare networking con altri professionisti del settore e scoprire le ultime tendenze nel mondo degli affitti brevi e della gestione immobiliare.'
                : 'Participate in our training events to expand your knowledge, network with other industry professionals and discover the latest trends in the world of short-term rentals and property management.'}
            </Description>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Iscriviti alla Newsletter' : 'Subscribe to the Newsletter'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default TrainingEvents; 